import { Input } from "@/components/ui/input"
import { Typography } from "@/components/ui/typography"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { OrgLogoSection } from "../../components/OrgLogoSection"
import {
  clampNumber,
  SettingsNumberInput,
  SettingsSwitchField,
} from "../../components/SettingsControls"
import {
  SettingsField,
  SettingsPage,
  SettingsSection,
} from "../../components/SettingsPage"
import { SettingsWithPreview } from "../../components/SettingsPreview"
import { TemplateSaveButton } from "../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../components/TemplateSettingsLoader"
import { useSavedDraft } from "../../hooks/useSavedDraft"
import {
  useTemplateSettingsSave,
  type TemplateSettingsSaver,
} from "../../hooks/useTemplateSettingsSave"
import { SignatureBoxPreview } from "../../previews/SignatureBoxPreview"
import { useDeclarationDefaultsAccess } from "../../useSettingsVisibility"
import { DeclarationTemplatesSection } from "./DeclarationTemplatesSection"

// The ranges the backend accepts.
const WINDOW_DAYS = { min: 30, max: 3650 }
const SCOPE_PRODUCTS = { min: 1, max: 100_000 }

/** The place printed next to the issue date in every declaration's
 *  signature box; empty uses the city of the organization's address. */
function PlaceSection({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate } = useTranslation()
  const [place, setPlace] = useSavedDraft(saved.place)

  return (
    <SettingsSection
      title={translate("SETTINGS/DECLARATIONS/SIGNATURE_BLOCK_TITLE")}
      target="declarations.signatureBox"
      description={translate(
        "SETTINGS/DECLARATIONS/SIGNATURE_BLOCK_DESCRIPTION",
      )}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="place"
          // The backend trims too.
          dirty={place.trim() !== saved.place}
          patch={() => ({ place: place.trim() })}
        />
      }
    >
      <SettingsWithPreview preview={<SignatureBoxPreview place={place} />}>
        <SettingsField
          htmlFor="settings-declaration-place"
          label={translate("MC_ORG_SIG_PLACE_LABEL")}
        >
          <Input
            id="settings-declaration-place"
            value={place}
            onChange={(event) => setPlace(event.target.value)}
            placeholder={translate("MC_ORG_SIG_PLACE_PLACEHOLDER")}
            disabled={!saver.canEdit}
            className="sm:max-w-xs"
          />
        </SettingsField>
      </SettingsWithPreview>
    </SettingsSection>
  )
}

/** How long supplier data counts as current, and the validity printed on a
 *  declaration. */
function ValiditySection({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate } = useTranslation()
  const [freshnessDays, setFreshnessDays] = useSavedDraft(
    saved.declarationFreshnessDays,
  )
  const [printValidUntil, setPrintValidUntil] = useSavedDraft(
    saved.printValidUntil,
  )
  const [validityDays, setValidityDays] = useSavedDraft(saved.validityDays)
  const readOnly = !saver.canEdit
  const days = translate("SETTINGS/UNIT_DAYS")
  const freshness = clampNumber(freshnessDays, WINDOW_DAYS.min, WINDOW_DAYS.max)
  const validity = clampNumber(validityDays, WINDOW_DAYS.min, WINDOW_DAYS.max)

  return (
    <SettingsSection
      title={translate("SETTINGS/DECLARATIONS/VALIDITY_TITLE")}
      description={translate("SETTINGS/DECLARATIONS/VALIDITY_DESCRIPTION")}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="validity"
          dirty={
            freshness !== saved.declarationFreshnessDays ||
            printValidUntil !== saved.printValidUntil ||
            validity !== saved.validityDays
          }
          patch={() => ({
            declarationFreshnessDays: freshness,
            printValidUntil,
            validityDays: validity,
          })}
        />
      }
    >
      <SettingsField
        htmlFor="settings-freshness"
        label={translate("MC_TPL_DECL_FRESHNESS")}
        // What wins over this default, behind the (i): the declaration rollup
        // reads the customer's own window first.
        info={translate("SETTINGS/DECLARATIONS/FRESHNESS_OVERRIDE")}
        hint={translate("MC_TPL_DECL_FRESHNESS_HINT")}
      >
        <SettingsNumberInput
          id="settings-freshness"
          value={freshnessDays}
          {...WINDOW_DAYS}
          unit={days}
          disabled={readOnly}
          onChange={setFreshnessDays}
        />
      </SettingsField>
      <SettingsSwitchField
        id="settings-print-valid-until"
        label={translate("MC_TPL_DECL_PRINT_VALID")}
        checked={printValidUntil}
        disabled={readOnly}
        onCheckedChange={setPrintValidUntil}
      />
      {printValidUntil && (
        <SettingsField
          htmlFor="settings-validity"
          label={translate("MC_TPL_DECL_VALIDITY")}
        >
          <SettingsNumberInput
            id="settings-validity"
            value={validityDays}
            {...WINDOW_DAYS}
            unit={days}
            disabled={readOnly}
            onChange={setValidityDays}
          />
        </SettingsField>
      )}
    </SettingsSection>
  )
}

/** How many products one declaration may cover. */
function ScopeLimitsSection({
  saved,
  saver,
}: {
  saved: InquiryTemplateSettings
  saver: TemplateSettingsSaver
}) {
  const { translate } = useTranslation()
  const [warnAbove, setWarnAbove] = useSavedDraft(saved.scopeWarnSubjects)
  const [refuseAbove, setRefuseAbove] = useSavedDraft(saved.scopeMaxSubjects)
  const readOnly = !saver.canEdit
  const warn = clampNumber(warnAbove, SCOPE_PRODUCTS.min, SCOPE_PRODUCTS.max)
  const refuse = clampNumber(
    refuseAbove,
    SCOPE_PRODUCTS.min,
    SCOPE_PRODUCTS.max,
  )
  // A warning above the refusal could never show; the backend refuses it too.
  const invalid = warn > refuse

  return (
    <SettingsSection
      title={translate("MC_TPL_DECL_SCOPE_LIMITS")}
      description={translate("MC_TPL_DECL_SCOPE_LIMITS_HINT")}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="scope-limits"
          dirty={
            warn !== saved.scopeWarnSubjects ||
            refuse !== saved.scopeMaxSubjects
          }
          invalid={invalid}
          patch={() => ({ scopeWarnSubjects: warn, scopeMaxSubjects: refuse })}
        />
      }
    >
      <div className="flex flex-wrap gap-6">
        <SettingsField
          htmlFor="settings-scope-warn"
          label={translate("MC_TPL_DECL_SCOPE_WARN")}
        >
          <SettingsNumberInput
            id="settings-scope-warn"
            value={warnAbove}
            {...SCOPE_PRODUCTS}
            disabled={readOnly}
            invalid={invalid}
            describedBy={invalid ? "settings-scope-problem" : undefined}
            onChange={setWarnAbove}
          />
        </SettingsField>
        <SettingsField
          htmlFor="settings-scope-refuse"
          label={translate("MC_TPL_DECL_SCOPE_MAX")}
        >
          <SettingsNumberInput
            id="settings-scope-refuse"
            value={refuseAbove}
            {...SCOPE_PRODUCTS}
            disabled={readOnly}
            invalid={invalid}
            describedBy={invalid ? "settings-scope-problem" : undefined}
            onChange={setRefuseAbove}
          />
        </SettingsField>
      </div>
      {/* The fields' borders mark the problem; this says what it is. */}
      {invalid && (
        <Typography as="muted" id="settings-scope-problem">
          {translate("MC_TPL_DECL_SCOPE_INVALID")}
        </Typography>
      )}
    </SettingsSection>
  )
}

export function ConformityDeclarationsPage() {
  const { translate } = useTranslation()
  const templatesEnabled = useFeatureFlag("MC_DECLARATION_TEMPLATES")
  const signaturesEnabled = useFeatureFlag("ENABLE_ORG_SIGNATURES")
  // For whoever issues declarations or sends inquiries.
  const declarationDefaults = useDeclarationDefaultsAccess()
  // One saver for every Save button on the page, see useTemplateSettingsSave.
  const saver = useTemplateSettingsSave()
  const usesSettings = signaturesEnabled || declarationDefaults

  return (
    <SettingsPage
      title={translate("SETTINGS/DECLARATIONS/TITLE")}
      description={translate("SETTINGS/DECLARATIONS/DESCRIPTION")}
    >
      {templatesEnabled && <DeclarationTemplatesSection />}
      {/* The letterhead of every declaration. */}
      <OrgLogoSection shared />
      {usesSettings && (
        <TemplateSettingsLoader>
          {(settings) => (
            <>
              {signaturesEnabled && (
                <PlaceSection saved={settings} saver={saver} />
              )}
              {declarationDefaults && (
                <>
                  <ValiditySection saved={settings} saver={saver} />
                  <ScopeLimitsSection saved={settings} saver={saver} />
                </>
              )}
            </>
          )}
        </TemplateSettingsLoader>
      )}
    </SettingsPage>
  )
}
